import { Box, Button, Tab, Tabs } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as topsApi from '@/api/tourOperators';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { AssistedConfigEditor } from '../config/AssistedConfigEditor';
import { ChangeHistoryTab } from '../config/ChangeHistoryTab';
import { SaveConfigChangesDialog } from '../config/SaveConfigChangesDialog';
import { JsonEditorWidget, type JsonEditorHandle } from '@/components/JsonEditor/JsonEditorWidget';

// Height calculation: 100vh minus the chrome above/below the editor —
// AppBar (64) + mt:8 on main (64) + page header (~80) + outer tabs (~54) +
// inner tabs (~50) + save row (~52) = ~364; add buffer → 400px.
const EDITOR_HEIGHT = 'calc(100vh - 400px)';
const EDITOR_MIN_HEIGHT = '350px';

interface Props {
  to: TourOperatorDetail;
  onSaved: () => void;
  /** URL segments after ".../configuration": [subtab, changeId?]. */
  path: string[];
  /** Replace the URL segments after ".../configuration". */
  onNavigate: (sub: string[]) => void;
}

type Subtab = 'assisted' | 'raw' | 'history';

const SUBTABS: Subtab[] = ['assisted', 'raw', 'history'];

export function ConfigurationTab({ to, onSaved, path, onNavigate }: Props) {
  const subtab: Subtab = SUBTABS.includes(path[0] as Subtab) ? (path[0] as Subtab) : 'assisted';
  // Change-details dialog: /configuration/history/<changeId>
  const selectedChangeId =
    subtab === 'history' && path[1] && /^\d+$/.test(path[1]) ? Number(path[1]) : null;
  const editorRef = useRef<JsonEditorHandle>(null);
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canEdit = hasPerm('console_top_manage');

  // Lifted edit state — shared by Assisted (inline edits), Raw (initialValue
  // seed on tab re-mount), and Change History (merge target). The Save flow
  // diffs this against `to.config` (the persisted snapshot) and PATCHes the
  // delta with the user's audit comment.
  const initialConfig = (to.config as Record<string, unknown> | null) ?? {};
  const [localConfig, setLocalConfig] = useState<Record<string, unknown>>(initialConfig);

  // Reset edit state when the parent reloads with a different TO id, or when
  // the saved `to.config` changes (e.g. after a successful PATCH).
  useEffect(() => {
    setLocalConfig((to.config as Record<string, unknown> | null) ?? {});
  }, [to.id, to.config]);

  // Save flow shared by Raw tab (and previously by Assisted before it grew
  // its own button — now the Assisted button also flows through here).
  const [pending, setPending] = useState<Record<string, unknown> | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: {
      config: Record<string, unknown>;
      comment: string;
      ifUnmodifiedSince: string | null;
    }) =>
      topsApi.update(to.id, {
        config: body.config,
        comment: body.comment,
        if_unmodified_since: body.ifUnmodifiedSince,
      }),
    onSuccess: () => {
      snackbar.show('Configuration saved.', 'success');
      setPending(null);
      setSaveError(null);
      onSaved();
    },
    onError: (e: unknown) => {
      if (e instanceof ConsoleApiError) {
        if (e.status === 409) {
          setSaveError(e.body?.detail ?? 'Configuration has been modified — please refresh.');
        } else {
          setSaveError(e.message);
        }
      } else {
        setSaveError('Save failed.');
      }
    },
  });

  function handleRawSave() {
    if (!editorRef.current) return;
    let config: Record<string, unknown>;
    try {
      config = editorRef.current.getValue();
    } catch {
      snackbar.show('Fix JSON errors before saving.', 'error');
      return;
    }
    // Update lifted state so other subtabs (and the Save dialog's diff
    // computation) reflect the Raw editor's current value.
    setLocalConfig(config);
    setSaveError(null);
    setPending(config);
  }

  function handleMergeApplied(merged: Record<string, unknown>) {
    setLocalConfig(merged);
    snackbar.show('Selected keys merged into the current configuration. Review and save to persist.', 'success');
    // Switch to Assisted so the user can immediately see and tune the merge result.
    onNavigate(['assisted']);
  }

  /** Pull the Raw editor's current value into the lifted state.
   *
   *  The jsoneditor widget owns its content internally — without this sync,
   *  switching from Raw to Assisted (or History) would leave the lifted
   *  state stale and Raw-tab edits would be invisible to the other subtabs.
   *  Called from `handleSubtabChange` whenever the user leaves the Raw tab.
   *
   *  Returns `true` on success (lifted state updated). On invalid JSON the
   *  call surfaces a snackbar and returns `false` so the caller can refuse
   *  the tab switch and force the user to fix the syntax first.
   */
  function syncFromRawEditor(): boolean {
    if (!editorRef.current) return true;
    try {
      const json = editorRef.current.getValue();
      // Only push when the editor's value actually differs from what's
      // already lifted — avoids triggering an unnecessary re-render.
      if (JSON.stringify(json) !== JSON.stringify(localConfig)) {
        setLocalConfig(json);
      }
      return true;
    } catch {
      snackbar.show('Raw tab contains invalid JSON — fix the syntax before switching tabs.', 'error');
      return false;
    }
  }

  function handleSubtabChange(_: unknown, v: Subtab) {
    if (subtab === 'raw' && v !== 'raw') {
      if (!syncFromRawEditor()) return;
    }
    onNavigate([v]);
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={subtab}
          onChange={handleSubtabChange}
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5 } }}
        >
          <Tab value="assisted" label="Assisted" />
          <Tab value="raw" label="Raw" />
          <Tab value="history" label="Change history" />
        </Tabs>
      </Box>

      {subtab === 'assisted' && (
        <AssistedConfigEditor
          to={to}
          onSaved={onSaved}
          currentConfig={localConfig}
          setCurrentConfig={setLocalConfig}
        />
      )}

      {subtab === 'raw' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* The outer div controls height; inner div fills it completely. */}
          <Box
            sx={{
              height: EDITOR_HEIGHT,
              minHeight: EDITOR_MIN_HEIGHT,
              width: '100%',
              // Override jsoneditor's default border so it integrates with MUI
              '& .jsoneditor': {
                height: '100%',
                border: '1px solid rgba(0,0,0,0.23)',
                borderRadius: '4px',
              },
              '& .jsoneditor-menu': {
                borderRadius: '4px 4px 0 0',
              },
            }}
          >
            {/* JsonEditorWidget only reads `initialValue` on mount. The tab
             *  conditional remounts the widget on every tab switch, so the
             *  current localConfig (including any merges applied via the
             *  History tab) is picked up automatically. */}
            <JsonEditorWidget
              ref={editorRef}
              initialValue={localConfig}
              readOnly={!canEdit}
            />
          </Box>

          {canEdit && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleRawSave}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Saving…' : 'Save changes…'}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {subtab === 'history' && (
        <ChangeHistoryTab
          tourOperatorId={to.id}
          tourOperatorCode={to.code}
          currentConfig={localConfig}
          onMerge={handleMergeApplied}
          selectedChangeId={selectedChangeId}
          onSelectChange={(changeId) =>
            onNavigate(changeId === null ? ['history'] : ['history', String(changeId)])
          }
        />
      )}

      <SaveConfigChangesDialog
        open={pending !== null}
        tourOperatorId={to.id}
        originalConfig={(to.config as Record<string, unknown> | null) ?? {}}
        pendingConfig={pending ?? {}}
        loadedConfigTs={to.config_last_modified_ts}
        saving={mutation.isPending}
        saveError={saveError}
        onConfirm={({ comment, ifUnmodifiedSince }) =>
          pending && mutation.mutate({ config: pending, comment, ifUnmodifiedSince })
        }
        onCancel={() => { setPending(null); setSaveError(null); }}
      />
    </Box>
  );
}
